import React, { ReactNode } from "react";
import { Portal } from "react-portal";
import { Link } from "react-router-dom";
import styled, { createGlobalStyle, css } from "styled-components";
import { Button } from "./Button";
import { DOLogoNav } from "./DOLogoNav";
import { Text } from "./Text";
import { useBodyClass } from "./hooks/useBodyClass";

import { mediaQueries, style } from "./style";

const TOP_NAV_HEIGHT_DESKTOP = 70;

// We should avoid making this value configurable (and leave it as a constant)
// if possible. See these react-modal docs for the rationale:
// https://github.com/reactjs/react-modal/blob/b9cbd404c18122ee7bdb00bc08ff27bd5b5b6bda/docs/styles/classes.md#for-the-documentbody-and-html-tag
const BODY_OPEN_CLASS_NAME = "FullScreenWizard__Body--open";

const GlobalStyles = createGlobalStyle`
  .FullScreenWizard__Body--open {
    overflow: hidden;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: ${style.vars.zIndex.top};
  overflow: auto;
`;

const TopBar = styled.div<{ hasBottomBorder: boolean }>`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: ${TOP_NAV_HEIGHT_DESKTOP}px;
  background: ${style.colors.white};
  padding: 0 5px;
  ${({ hasBottomBorder }) =>
    hasBottomBorder &&
    css`
      border-bottom: 1px solid ${style.colors.shadesOfGrey["2"]};
    `}
`;

const Logo = styled(DOLogoNav).attrs({ isMobile: true })`
  margin-left: ${style.vars.space["4"]};
`;

const Title = styled(Text).attrs({ type: "h4" })`
  margin-top: 0;
  margin-bottom: 0;
  margin-left: ${style.vars.space["4"]};
`;

const CancelButtonContainer = styled.div`
  margin-left: auto;

  ${mediaQueries.mediumMin} {
    margin-right: ${style.vars.space["2"]};
  }
`;

const StepBar = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: ${style.vars.space["9"]};
  background: ${style.colors.blue.darkest};
`;

const MobileStepContainer = styled.div`
  text-align: center;
  flex-grow: 1;
`;

const MobileStepCount = styled(Text).attrs({ type: "small" })`
  color: ${style.colors.white};
`;

const MobileStepTitle = styled.div`
  color: ${style.colors.white};
  font-weight: ${style.vars.fontWeight.bold};
`;

const DesktopStepHeading = styled(Text).attrs({ type: "h4" })`
  color: ${style.colors.white};
  margin-top: 0;
  margin-bottom: 0;
  margin-left: ${style.vars.space["5"]};
`;

const ContentContainer = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background: ${style.colors.white};

  ${mediaQueries.largeMin} {
    flex-direction: row;
  }
`;

export const PrimaryContent = styled.div`
  padding: ${style.vars.space["3"]};
  background-color: ${style.colors.white};

  ${mediaQueries.largeMin} {
    flex-grow: 1;
    padding: ${style.vars.space["5"]};
  }
`;

export const SecondaryContent = styled.div`
  flex-shrink: 0;
  background: ${style.colors.grey.lightest};
  padding: ${style.vars.space["3"]};
  border-top: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
  border-bottom: ${style.vars.borderWidth.base} solid ${style.colors.grey.light};

  ${mediaQueries.largeMin} {
    width: 22rem;
    padding: ${style.vars.space["5"]};
    border-bottom: none;
    border-left: ${style.vars.borderWidth.thin} solid ${style.colors.grey.light};
  }
`;

type FullScreenWizardProps = {
  title: string;
  routeOnCancel?: string;
  onCancel?: () => void;
  currentStepTitlePrefix?: string;
  currentStepTitle?: string;
};

export const FullScreenWizard: React.FC<FullScreenWizardProps> = ({
  title,
  routeOnCancel,
  onCancel,
  currentStepTitlePrefix,
  currentStepTitle,
  children,
}) => {
  const isMobile = mediaQueries.mediumMax.useMatchesBreakpoint();

  // This is necessary for preventing double scrollbars from appearing. I stole
  // the technique from react-modal:
  // https://github.com/reactjs/react-modal/blob/b9cbd404c18122ee7bdb00bc08ff27bd5b5b6bda/docs/styles/classes.md#for-the-documentbody-and-html-tag
  useBodyClass(BODY_OPEN_CLASS_NAME);

  let cancelButton: ReactNode;

  if (routeOnCancel) {
    cancelButton = (
      <CancelButtonContainer>
        <Link to={routeOnCancel}>{isMobile ? <Button variation="icon" icon="close" /> : <Button variation="tertiary">Cancel</Button>}</Link>
      </CancelButtonContainer>
    );
  } else if (onCancel) {
    cancelButton = (
      <CancelButtonContainer>
        {isMobile ? (
          <Button variation="icon" icon="close" onClick={onCancel} />
        ) : (
          <Button variation="tertiary" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </CancelButtonContainer>
    );
  }

  return (
    <>
      <GlobalStyles />
      <Portal>
        <Container>
          <TopBar hasBottomBorder={!currentStepTitle}>
            <Logo />
            <Title>{title}</Title>
            {cancelButton}
          </TopBar>
          {currentStepTitle && (
            <StepBar>
              {isMobile ? (
                <MobileStepContainer>
                  {currentStepTitlePrefix && <MobileStepCount>{currentStepTitlePrefix}</MobileStepCount>}
                  <MobileStepTitle>{currentStepTitle}</MobileStepTitle>
                </MobileStepContainer>
              ) : (
                <DesktopStepHeading>
                  {currentStepTitlePrefix ? `${currentStepTitlePrefix}: ${currentStepTitle}` : currentStepTitle}
                </DesktopStepHeading>
              )}
            </StepBar>
          )}
          <ContentContainer>{children}</ContentContainer>
        </Container>
      </Portal>
    </>
  );
};
